import { useEffect, useState, useMemo } from "react";
import { Gift, IndianRupee, Sparkles, Users, TrendingUp, Clock, CheckCircle2, ChevronRight, ArrowLeft, MapPin, Phone } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import useLocations from "@/hooks/useLocations";
import { LoanIcon, findLoan, loanRangeLabel, clampAmount } from "@/lib/loanUtils";

const inrCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2).replace(/\.00$/, "")} L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `₹${v.toLocaleString("en-IN")}`;
};

const inrFull = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} · ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return "—"; }
}

// ============================================================================
// Earnings Dashboard (Gold Premium Card)
// ============================================================================
function EarningsDashboard({ stats }) {
  return (
    <div
      data-testid="refer-earnings-card"
      className="relative rounded-2xl p-4 overflow-hidden border-2 border-[#D4AF37]/60"
      style={{
        background: "linear-gradient(140deg, #1a1305 0%, #2b1f08 45%, #3a2a0a 100%)",
        boxShadow: "0 8px 32px -8px rgba(212,175,55,0.35), inset 0 1px 0 rgba(212,175,55,0.2)",
      }}
    >
      {/* Sparkle accents */}
      <div className="absolute -top-8 -right-6 w-24 h-24 rounded-full bg-[#D4AF37]/10 blur-2xl"/>
      <div className="absolute -bottom-10 -left-4 w-24 h-24 rounded-full bg-[#F97316]/10 blur-2xl"/>

      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#8a6f1c] flex items-center justify-center text-slate-900 shadow-lg">
            <TrendingUp className="w-5 h-5"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">My Referral</div>
            <div className="text-white font-heading text-lg leading-tight">Earnings</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]/70">Rate</div>
            <div className="font-mono-alt text-[#D4AF37] font-bold text-sm">1% flat</div>
          </div>
        </div>

        {/* Row 1: 3 boxes */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          <StatBox
            label="Total Referred"
            value={String(stats.total_referred || 0)}
            sub="Cases"
            tone="dim"
            testId="stat-total-referred"
          />
          <StatBox
            label="Total Loan"
            value={inrCompact(stats.total_loan_amount || 0)}
            sub="Amount"
            tone="dim"
            testId="stat-total-loan"
          />
          <StatBox
            label="Total Commission"
            value={inrCompact(stats.total_commission || 0)}
            sub="@ 1%"
            tone="gold"
            large
            testId="stat-total-commission"
          />
        </div>

        {/* Row 2: 2 boxes */}
        <div className="grid grid-cols-2 gap-2">
          <StatBox
            label="Pending"
            value={inrFull(stats.pending_commission || 0)}
            sub="Awaiting Disbursal"
            tone="orange"
            testId="stat-pending-commission"
          />
          <StatBox
            label="Paid Commission"
            value={inrFull(stats.paid_commission || 0)}
            sub="Credited to Account"
            tone="green"
            testId="stat-paid-commission"
          />
        </div>

        <div className="mt-3 text-center text-[10px] text-[#D4AF37]/80 font-mono-alt tracking-wide">
          ₹5L = ₹5K · ₹10L = ₹10K · No Cap!
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, sub, tone = "dim", large = false, testId }) {
  const bg = {
    gold: "bg-[#D4AF37]/15 border-[#D4AF37]/60",
    orange: "bg-[#F97316]/12 border-[#F97316]/50",
    green: "bg-[#22C55E]/12 border-[#22C55E]/50",
    dim: "bg-black/25 border-white/10",
  }[tone];
  const valueColor = {
    gold: "text-[#F4CF52]",
    orange: "text-[#FDB979]",
    green: "text-[#4ADE80]",
    dim: "text-white",
  }[tone];
  return (
    <div data-testid={testId} className={`rounded-xl border p-2.5 ${bg}`}>
      <div className="text-[9px] uppercase tracking-widest text-white/60 font-bold leading-tight">{label}</div>
      <div className={`font-mono-alt font-black mt-1 leading-none ${valueColor} ${large ? "text-xl" : "text-base"}`}>{value}</div>
      <div className="text-[9px] text-white/50 mt-1 leading-tight">{sub}</div>
    </div>
  );
}

// ============================================================================
// Referral Detail Tracking Page
// ============================================================================
function CommissionBadge({ paid, size = "sm" }) {
  const cls = paid
    ? "bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/50"
    : "bg-[#F97316]/20 text-[#FDB979] border-[#F97316]/50";
  const dotCls = paid ? "bg-[#4ADE80]" : "bg-[#FDB979]";
  const sizeCls = size === "lg" ? "text-xs px-3 py-1.5" : "text-[10px] px-2 py-1";
  return (
    <span data-testid={paid ? "commission-badge-paid" : "commission-badge-pending"} className={`inline-flex items-center gap-1.5 rounded-full border font-bold uppercase tracking-widest ${cls} ${sizeCls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls} ${paid ? "" : "animate-pulse"}`}/>
      {paid ? "Commission Paid ✓" : "Pending ⏳"}
    </span>
  );
}

function ReferralDetailPage({ leadId, onBack }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/bank-staff/refer/${leadId}`)
      .then(r => setD(r.data))
      .catch(e => { toast.error(errorMessage(e)); onBack(); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  if (loading || !d) {
    return (
      <div className="text-center text-slate-500 py-12" data-testid="refer-detail-loading">Loading…</div>
    );
  }

  return (
    <div className="space-y-3" data-testid="referral-detail-page">
      <button
        type="button"
        onClick={onBack}
        data-testid="refer-detail-back"
        className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm py-1"
      >
        <ArrowLeft className="w-4 h-4"/> Back to Refer
      </button>

      {/* Top card */}
      <div
        className="rounded-2xl p-4 border-2 border-[#D4AF37]/50"
        style={{ background: "linear-gradient(140deg, #1a1305 0%, #2b1f08 100%)" }}
        data-testid="detail-top-card"
      >
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">Referred Customer</div>
        <div className="font-heading text-2xl text-white mt-0.5 break-words">{d.name}</div>
        {d.phone && (
          <a href={`tel:${d.phone}`} className="inline-flex items-center gap-1.5 text-emerald-300 text-sm mt-1 font-mono-alt">
            <Phone className="w-3.5 h-3.5"/>{d.phone}
          </a>
        )}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-black/30 border border-white/10 p-2.5">
            <div className="text-[9px] uppercase tracking-widest text-white/50 font-bold">Reference ID</div>
            <div className="text-[#D4AF37] font-mono-alt font-bold mt-0.5 text-xs" data-testid="detail-lead-id">{d.lead_id}</div>
          </div>
          <div className="rounded-lg bg-black/30 border border-white/10 p-2.5">
            <div className="text-[9px] uppercase tracking-widest text-white/50 font-bold">Loan Amount</div>
            <div className="text-white font-mono-alt font-bold mt-0.5 text-sm">{inrFull(d.amount)}</div>
          </div>
        </div>
        <div className="mt-2 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 p-2.5 flex items-center justify-between gap-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#D4AF37] font-bold">Your Commission @ 1%</div>
            <div className="text-[#F4CF52] font-mono-alt font-black text-xl mt-0.5">{inrFull(d.commission_amount)}</div>
          </div>
          <CommissionBadge paid={d.commission_paid} size="lg"/>
        </div>
      </div>

      {/* Timeline */}
      <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4" data-testid="detail-timeline">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-3">Tracking Status</div>
        <div className="relative pl-6">
          <div className="absolute top-2 bottom-2 left-[11px] w-0.5 bg-slate-800"/>
          {d.timeline.map((s, i) => (
            <TimelineRow key={i} step={s} last={s.label === "Disbursed"}/>
          ))}
        </div>
      </div>

      {/* Commission banner */}
      {d.commission_paid ? (
        <div
          data-testid="detail-commission-banner-paid"
          className="rounded-2xl border-2 border-[#22C55E]/60 p-4 text-center"
          style={{
            background: "linear-gradient(140deg, #052e18 0%, #0a4023 100%)",
            boxShadow: "0 0 24px -4px rgba(34,197,94,0.35), inset 0 1px 0 rgba(74,222,128,0.15)",
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4ADE80] font-bold">Your Commission</div>
          <div className="font-heading text-2xl text-white mt-1">{inrFull(d.commission_amount)} Paid <span className="text-[#4ADE80]">✓</span></div>
          <div className="text-xs text-[#4ADE80]/80 mt-1">Credited on {fmtDateTime(d.commission_paid_at)}</div>
        </div>
      ) : (
        <div
          data-testid="detail-commission-banner-pending"
          className="rounded-2xl border-2 border-[#F97316]/50 p-4 text-center"
          style={{
            background: "linear-gradient(140deg, #2b1508 0%, #3a1e08 100%)",
            boxShadow: "0 0 20px -4px rgba(249,115,22,0.3)",
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F97316] font-bold">Your Pending Commission</div>
          <div className="font-heading text-2xl text-white mt-1">{inrFull(d.commission_amount)}</div>
          <div className="text-xs text-[#FDB979] mt-1">Will be credited after admin confirmation</div>
        </div>
      )}

      <div className="text-center text-[10px] text-slate-600 uppercase tracking-widest pt-2 pb-1">
        Updated {fmtDateTime(d.updated_at)} IST
      </div>
    </div>
  );
}

function TimelineRow({ step, last }) {
  const done = step.done;
  const active = step.active;
  const isDisbursed = step.label === "Disbursed";
  const dotCls = done
    ? (isDisbursed ? "bg-gradient-to-br from-[#D4AF37] to-[#8a6f1c] text-slate-900 shadow-lg shadow-[#D4AF37]/40" : "bg-[#22C55E] text-slate-900")
    : active ? "bg-[#F97316] text-slate-900 animate-pulse"
    : "bg-slate-800 text-slate-600 border border-slate-700";
  const textCls = done
    ? (isDisbursed ? "text-[#D4AF37]" : "text-emerald-300")
    : active ? "text-[#F97316]"
    : "text-slate-500";

  return (
    <div className="relative pb-4 last:pb-0">
      <div className={`absolute -left-6 top-0 w-6 h-6 rounded-full flex items-center justify-center ${dotCls}`}>
        {done ? <CheckCircle2 className="w-3.5 h-3.5"/> : active ? <Clock className="w-3.5 h-3.5"/> : <span className="w-1.5 h-1.5 rounded-full bg-slate-600"/>}
      </div>
      <div className={`text-sm font-bold ${textCls}`}>{step.label}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">
        {done ? `Completed · ${fmtDateTime(step.at)}` : active ? "In Progress" : "Pending"}
      </div>
    </div>
  );
}

// ============================================================================
// Add Referral Dialog
// ============================================================================
function AddReferralDialog({ open, onClose, onDone }) {
  const { user } = useAuth();
  const { districts } = useLocations();
  const [f, setF] = useState({ name: "", phone: "", district: "", loan_type: "", amount: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [loans, setLoans] = useState([]);
  useEffect(() => {
    // Loan types from Admin → Loans master (matches admin Loans page).
    api.get("/loans").then(r => setLoans(r.data || [])).catch(() => setLoans([]));
  }, []);

  const referredFromLabel = useMemo(() => {
    const staffLabel = user?.bank_name ? "BM" : "STAFF"; // fallback labels
    const roleLabel = "Bank Staff";
    const district = user?.district || "";
    const shortId = (user?.id || "").slice(-6).toUpperCase() || "STAFF";
    return `${roleLabel} - ${district || user?.name || "—"} - ${shortId}`;
  }, [user]);

  const submit = async () => {
    if (!f.name || f.phone.length !== 10 || !f.district || !f.loan_type || !f.amount) {
      toast.error("Fill all required fields correctly");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/staff/referral-lead", {
        ...f,
        amount: parseFloat(f.amount),
        referred_from: referredFromLabel,
      });
      const commission = Math.round(parseFloat(f.amount) * 0.01);
      toast.success(`Referral created: ${data.lead_id} · Est. commission ₹${commission.toLocaleString("en-IN")}`);
      onClose();
      setF({ name: "", phone: "", district: "", loan_type: "", amount: "", notes: "" });
      onDone && onDone();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  const estCommission = useMemo(() => {
    const amt = parseFloat(f.amount || "0");
    return isNaN(amt) ? 0 : Math.round(amt * 0.01);
  }, [f.amount]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F172A] border-[#D4AF37]/30 text-white max-h-[92vh] overflow-y-auto max-w-md" data-testid="add-referral-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#D4AF37]">
            <Gift className="w-5 h-5"/> Add Referral Customer
          </DialogTitle>
          <DialogDescription className="sr-only">Add a referral and earn 1% commission on disbursal.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          {/* Auto-filled Referred From */}
          <div>
            <Label className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold">Referred From (Auto-filled)</Label>
            <div
              data-testid="ref-from-locked"
              className="mt-1.5 h-11 rounded-md bg-[#0A0E1E] border border-[#D4AF37]/40 px-3 flex items-center text-[#D4AF37] font-mono-alt text-sm cursor-not-allowed opacity-90"
            >
              {referredFromLabel}
            </div>
          </div>

          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Customer Name *</Label>
            <Input data-testid="ref-name" placeholder="Full name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="bg-[#0A0E1E] border-slate-700 mt-1.5 h-11"/>
          </div>

          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Phone (10 digits) *</Label>
            <Input data-testid="ref-phone" placeholder="10-digit mobile" inputMode="numeric" value={f.phone} maxLength={10} onChange={e => setF({ ...f, phone: e.target.value.replace(/\D/g, "") })} className="bg-[#0A0E1E] border-slate-700 mt-1.5 h-11 font-mono-alt"/>
          </div>

          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">District *</Label>
            <Select value={f.district} onValueChange={v => setF({ ...f, district: v })}>
              <SelectTrigger data-testid="ref-district" className="bg-[#0A0E1E] border-slate-700 mt-1.5 h-11"><SelectValue placeholder="Select district"/></SelectTrigger>
              <SelectContent className="bg-[#0F172A] text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                {districts.length === 0 ? <div className="p-3 text-sm text-slate-400">No districts configured. Contact admin.</div> : districts.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Loan Type *</Label>
            <Select value={f.loan_type} onValueChange={v => setF({ ...f, loan_type: v })}>
              <SelectTrigger data-testid="ref-loan-type" className="bg-[#0A0E1E] border-slate-700 mt-1.5 h-11"><SelectValue placeholder="Select loan type"/></SelectTrigger>
              <SelectContent className="bg-[#0F172A] text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                {loans.length === 0
                  ? <div className="p-3 text-sm text-slate-400">No loans configured. Contact admin.</div>
                  : loans.map(l => <SelectItem key={l.id || l.name} value={l.name}><span className="inline-flex items-center gap-2"><LoanIcon loan={l} className="w-4 h-4 text-[#D4AF37]"/>{l.name}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Loan Amount (₹) *</Label>
            <div className="relative mt-1.5">
              <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
              <Input
                data-testid="ref-amount"
                placeholder="e.g. 500000"
                inputMode="numeric"
                value={f.amount}
                onChange={e => setF({ ...f, amount: e.target.value.replace(/\D/g, "") })}
                onBlur={()=>{ const c = clampAmount(f.amount, findLoan(loans, f.loan_type)); if (c.changed) { setF({...f, amount: c.value}); toast.message(c.why); } }}
                className="bg-[#0A0E1E] border-slate-700 pl-9 h-11 font-mono-alt"/>
            </div>
            {f.loan_type && loanRangeLabel(findLoan(loans, f.loan_type)) && (
              <div className="mt-1 text-[11px] text-[#D4AF37]/80 font-mono-alt" data-testid="ref-amount-range">{loanRangeLabel(findLoan(loans, f.loan_type))}</div>
            )}
            {estCommission > 0 && (
              <div className="mt-1.5 text-[11px] text-[#D4AF37] font-mono-alt">Est. commission: ₹{estCommission.toLocaleString("en-IN")} (1%)</div>
            )}
          </div>

          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Notes (Optional)</Label>
            <Textarea data-testid="ref-notes" placeholder="Any additional context" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} className="bg-[#0A0E1E] border-slate-700 mt-1.5"/>
          </div>

          {/* New commission note (replaces ₹500 bonus text) */}
          <div className="rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/40 p-3 text-xs text-[#D4AF37]/90 leading-relaxed" data-testid="commission-note">
            <div className="font-bold text-[#D4AF37] mb-1">You will earn 1% Commission</div>
            <div>Example: ₹5,00,000 loan = <b className="text-[#F4CF52]">₹5,000</b> commission.</div>
            <div className="mt-1 text-[10px] text-[#D4AF37]/70">Commission paid after disbursal. TDS applicable.</div>
          </div>

          <Button
            data-testid="ref-submit"
            disabled={submitting}
            onClick={submit}
            className="w-full h-12 bg-[#F97316] hover:bg-[#ea6a10] text-white font-bold rounded-full text-sm shadow-lg shadow-orange-500/25"
          >
            <Sparkles className="w-4 h-4 mr-2"/> {submitting ? "Submitting…" : "Submit Referral"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Root Refer Page
// ============================================================================
export default function BankStaffReferPage() {
  const [stats, setStats] = useState({ total_referred: 0, total_loan_amount: 0, total_commission: 0, pending_commission: 0, paid_commission: 0 });
  const [items, setItems] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/bank-staff/refer/stats"),
        api.get("/bank-staff/refer/list"),
      ]);
      setStats(s.data);
      setItems(l.data.items || []);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (detailId) {
    return <ReferralDetailPage leadId={detailId} onBack={() => { setDetailId(null); load(); }}/>;
  }

  return (
    <div className="space-y-3" data-testid="bank-staff-refer-page">
      <EarningsDashboard stats={stats}/>

      {/* Gold banner */}
      <div
        className="rounded-2xl border-2 border-[#D4AF37]/60 p-4 relative overflow-hidden"
        style={{
          background: "linear-gradient(90deg, #2b1f08 0%, #3a2a0a 50%, #2b1f08 100%)",
        }}
        data-testid="refer-banner"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#D4AF37] flex items-center justify-center shrink-0 text-slate-900 shadow-lg">
            <Gift className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[#D4AF37] font-heading font-bold text-sm leading-tight">Refer a customer · Earn 1% Commission</div>
            <div className="text-xs text-[#D4AF37]/70 mt-1 leading-snug">on Disbursed Loan Amount · Unlimited Earnings · No Cap · Grow as You Refer</div>
          </div>
        </div>
        <Button
          data-testid="open-add-referral-btn"
          onClick={() => setDlgOpen(true)}
          className="mt-3 w-full h-12 bg-[#F97316] hover:bg-[#ea6a10] text-white font-black rounded-full text-sm shadow-lg shadow-orange-500/30"
        >
          + Add Referral Customer · Earn 1%
        </Button>
      </div>

      {/* Referred customers list */}
      <div>
        <div className="flex items-center justify-between px-1 mb-2 mt-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5"/> Referred Customers
          </div>
          <div className="text-xs font-mono-alt text-slate-400">{items.length}</div>
        </div>

        {loading ? (
          <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-6 text-center text-slate-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-8 text-center" data-testid="refer-empty">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-3">
              <Gift className="w-6 h-6 text-[#D4AF37]"/>
            </div>
            <div className="text-white font-bold">No referrals yet</div>
            <div className="text-slate-500 text-xs mt-1">Refer your first customer and earn 1% on disbursal.</div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="refer-list">
            {items.map(it => (
              <ReferralRow key={it.lead_id} item={it} onOpen={() => setDetailId(it.lead_id)}/>
            ))}
          </div>
        )}
      </div>

      <AddReferralDialog open={dlgOpen} onClose={() => setDlgOpen(false)} onDone={load}/>
    </div>
  );
}

function ReferralRow({ item, onOpen }) {
  const inits = (item.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`refer-row-${item.lead_id}`}
      className="w-full text-left rounded-2xl bg-[#141A2E] border border-[#1E2742] hover:border-[#D4AF37]/40 p-3 active:scale-[0.99] transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8a6f1c] flex items-center justify-center text-slate-900 font-black text-sm shrink-0">
          {inits}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-sm truncate">{item.name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span className="font-mono-alt">Loan {inrFull(item.amount)}</span>
            <span>·</span>
            <span className={item.disbursed ? "text-emerald-400 font-bold" : "text-[#F97316]"}>{item.disbursed ? "Disbursed" : (item.status || "In Progress").replace(/_/g, " ")}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]/70">Commission</div>
          <div className="text-[#F4CF52] font-mono-alt font-black text-sm">{inrFull(item.commission_amount)}</div>
          <div className="mt-1"><CommissionBadge paid={item.commission_paid}/></div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-600 shrink-0"/>
      </div>
    </button>
  );
}
